/* ============================================================
   CART DRAWER PRO - v2
   AJAX cart, free gift goal, Shopify related products,
   automatic savings and delivery estimate.
   ============================================================ */

(function () {
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const SECTION_ID = 'cart-drawer-pro';           // must match the section filename
  const RECO_SECTION_ID = 'cart-drawer-pro-reco'; // must match the cards section filename
  const CURRENCY = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';

  class CartDrawerPro extends HTMLElement {
    connectedCallback() {
      this.busy = false;
      this.bind();
      this.renderEta();
      this.loadReco();
      this.syncGift();

      if (!window.__cdpGlobalsBound) {
        this.bindGlobals();
        window.__cdpGlobalsBound = true;
      }
    }

    /* ---------- wiring ---------- */

    bind() {
      if (this._bound) this.removeEventListener('click', this._bound);

      this._bound = (e) => {
        const close = e.target.closest('[data-cdp-close]');
        if (close) { e.preventDefault(); return this.close(); }

        const qty = e.target.closest('[data-cdp-qty]');
        if (qty) {
          e.preventDefault();
          const line = qty.closest('[data-line]').dataset.line;
          return this.changeLine(line, parseInt(qty.dataset.cdpQty, 10));
        }

        const add = e.target.closest('[data-cdp-add]');
        if (add) {
          e.preventDefault();
          e.stopPropagation();
          return this.addVariant(add.dataset.cdpAdd, add);
        }

        const scroll = e.target.closest('[data-cdp-scroll]');
        if (scroll) {
          e.preventDefault();
          const track = this.querySelector('[data-cdp-track]');
          if (!track) return;
          const card = track.querySelector('.cdp__card');
          const step = card ? card.offsetWidth + 2 : 140;
          track.scrollBy({ left: step * 2 * parseInt(scroll.dataset.cdpScroll, 10), behavior: 'smooth' });
        }
      };

      this.addEventListener('click', this._bound);

      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-cdp-qty-input]');
        if (!input) return;
        const line = input.closest('[data-line]').dataset.line;
        this.changeLine(line, Math.max(0, parseInt(input.value, 10) || 0));
      });

      const track = this.querySelector('[data-cdp-track]');
      if (track) track.addEventListener('scroll', () => this.syncArrows(), { passive: true });
    }

    bindGlobals() {
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-cart-drawer-open], a[href$="/cart"], a[href*="/cart?"]');
        if (trigger) { e.preventDefault(); document.getElementById('CartDrawerPro')?.open(); }
      });
      document.addEventListener('cart-drawer:open', () => document.getElementById('CartDrawerPro')?.open());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') document.getElementById('CartDrawerPro')?.close();
      });

      document.addEventListener('submit', async (e) => {
        const form = e.target.closest('form[action*="/cart/add"]');
        if (!form) return;
        const drawer = document.getElementById('CartDrawerPro');
        if (!drawer || drawer.dataset.autoOpen !== 'true') return;

        e.preventDefault();
        const btn = form.querySelector('[type="submit"]');
        btn && btn.setAttribute('aria-busy', 'true');
        try {
          const res = await fetch(`${ROOT}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form)
          });
          if (!res.ok) throw new Error(await res.text());
          await drawer.refresh();
          drawer.open();
        } catch (err) {
          console.error('[cart-drawer-pro]', err);
          form.submit();
        } finally {
          btn && btn.removeAttribute('aria-busy');
        }
      });
    }

    /* ---------- open / close ---------- */

    open() {
      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-open'));
      this.scrollLock(true);
      this.querySelector('.cdp__close')?.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('cart-drawer:opened'));
    }

    close() {
      this.classList.remove('is-open');
      this.scrollLock(false);
      const done = () => { this.hidden = true; };
      const panel = this.querySelector('.cdp__panel');
      panel ? panel.addEventListener('transitionend', done, { once: true }) : done();
    }

    scrollLock(on) {
      if (on) {
        this._scrollY = window.scrollY;
        document.body.style.cssText += `position:fixed;top:-${this._scrollY}px;left:0;right:0;overflow:hidden;`;
      } else {
        ['position', 'top', 'left', 'right', 'overflow'].forEach((p) => (document.body.style[p] = ''));
        if (this._scrollY != null) window.scrollTo(0, this._scrollY);
      }
    }

    /* ---------- cart mutations ---------- */

    async changeLine(line, quantity) {
      await this.mutate(`${ROOT}cart/change.js`, { line: Number(line), quantity });
    }

    async addVariant(id, button) {
      const label = button && button.textContent;
      if (button) button.textContent = '...';
      await this.mutate(`${ROOT}cart/add.js`, { items: [{ id: Number(id), quantity: 1 }] });
      if (button && button.isConnected) button.textContent = label;
    }

    async mutate(url, body) {
      if (this.busy) return;
      this.busy = true;
      this.classList.add('is-busy');
      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });
        if (!res.ok) throw new Error(await res.text());
        await this.refresh();
      } catch (err) {
        console.error('[cart-drawer-pro]', err);
      } finally {
        this.busy = false;
        this.classList.remove('is-busy');
      }
    }

    /* ---------- re-render ---------- */

    async refresh() {
      const res = await fetch(`${ROOT}?sections=${SECTION_ID}`);
      const data = await res.json();
      const html = new DOMParser().parseFromString(data[SECTION_ID], 'text/html');
      const fresh = html.querySelector('cart-drawer-pro');
      if (!fresh) return;

      const wasOpen = this.classList.contains('is-open');
      this.innerHTML = fresh.innerHTML;
      Object.keys(fresh.dataset).forEach((k) => (this.dataset[k] = fresh.dataset[k]));

      this.bind();
      this.renderEta();
      if (wasOpen) this.classList.add('is-open');

      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { paidCount: parseInt(this.dataset.paidCount, 10) || 0 }
      }));

      await this.loadReco();
      await this.syncGift();
    }

    /* ---------- free gift ---------- */

    async syncGift() {
      const variantId = parseInt(this.dataset.giftVariant, 10);
      if (!variantId || this._giftBusy) return;

      const threshold = parseInt(this.dataset.threshold, 10) || 0;
      const paid = parseInt(this.dataset.paidCount, 10) || 0;

      const cart = await (await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } })).json();
      const giftIndex = cart.items.findIndex((i) => i.variant_id === variantId);
      const unlocked = paid >= threshold;

      if (unlocked && giftIndex === -1) {
        this._giftBusy = true;
        await fetch(`${ROOT}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: { _free_gift: 'true' } }] })
        });
        this._giftBusy = false;
        await this.refresh();
      } else if (!unlocked && giftIndex > -1) {
        this._giftBusy = true;
        await fetch(`${ROOT}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: giftIndex + 1, quantity: 0 })
        });
        this._giftBusy = false;
        await this.refresh();
      }
    }

    /* ---------- related products ----------
       Uses Shopify's own recommendation engine, seeded with the
       first product added to the cart.                          */

    async loadReco() {
      const wrap = this.querySelector('[data-cdp-reco]');
      let track = this.querySelector('[data-cdp-track]');
      if (!wrap || !track) return;

      const seed = this.dataset.seedProduct;
      if (!seed) { wrap.hidden = true; return; }

      const limit = parseInt(this.dataset.recoLimit, 10) || 8;
      const url = `${ROOT}recommendations/products?section_id=${RECO_SECTION_ID}&product_id=${seed}&limit=${limit}&intent=related`;

      try {
        const html = await (await fetch(url)).text();
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cdp-track]');

        if (!fresh || !fresh.querySelector('.cdp__card')) { wrap.hidden = true; return; }

        track.replaceWith(fresh);
        track = fresh;

        const label = this.dataset.addLabel || 'ADD TO CART';
        track.querySelectorAll('.cdp__card-add').forEach((b) => (b.textContent = label));
        track.querySelectorAll('.cdp__card-plus').forEach((b) => b.setAttribute('aria-label', label));

        track.addEventListener('scroll', () => this.syncArrows(), { passive: true });
        wrap.hidden = false;
        this.syncArrows();
      } catch (err) {
        console.error('[cart-drawer-pro]', err);
        wrap.hidden = true;
      }
    }

    /* ---------- expected delivery ---------- */

    renderEta() {
      const el = this.querySelector('[data-cdp-eta]');
      if (!el) return;

      const lead = parseInt(this.dataset.leadDays, 10) || 3;
      const cutoff = parseInt(this.dataset.cutoffHour, 10);
      const d = new Date();

      let remaining = lead + (d.getHours() >= cutoff ? 1 : 0);
      while (remaining > 0) {
        d.setDate(d.getDate() + 1);
        const day = d.getDay();
        if (day !== 0 && day !== 6) remaining--;
      }

      const locale = (window.Shopify && window.Shopify.locale) || document.documentElement.lang || 'en';
      el.textContent = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(d).toUpperCase();
    }

    /* ---------- carousel arrows ---------- */

    syncArrows() {
      const track = this.querySelector('[data-cdp-track]');
      if (!track) return;
      const prev = this.querySelector('[data-cdp-scroll="-1"]');
      const next = this.querySelector('[data-cdp-scroll="1"]');
      const max = track.scrollWidth - track.clientWidth - 1;
      if (prev) prev.disabled = track.scrollLeft <= 0;
      if (next) next.disabled = track.scrollLeft >= max;
    }
  }

  if (!customElements.get('cart-drawer-pro')) {
    customElements.define('cart-drawer-pro', CartDrawerPro);
  }
})();